import { parseFlightNumberInput } from './flightJourneyUtils';
import { normalizeDateTimeLocal } from './flightScheduleUtils';
import { resolveAirlineIata } from './airlineIata';
import transportationAPI from '../services/transportationAPI';

export const legNeedsRouteLookup = (leg) => {
  if (!leg?.departure_airport || !leg?.arrival_airport) {
    return true;
  }
  return leg.departure_airport === leg.arrival_airport;
};

/**
 * Resolve IATA + number from either "UA2775", separate UA + 2775 fields,
 * or airline display names like "Emirates" + "226".
 */
export const resolveFlightLegIdentity = (leg) => {
  const fromNumber = parseFlightNumberInput(leg?.flight_number)
    || parseFlightNumberInput(String(leg?.flight_number || '').replace(/\s+/g, ''));
  if (fromNumber?.airline && fromNumber?.flight_number) {
    return fromNumber;
  }

  const digits = String(leg?.flight_number || '').replace(/\D/g, '');
  const iata = resolveAirlineIata(leg?.airline, leg?.airline_code)
    || resolveAirlineIata(String(leg?.airline || '').split(/\s+/).slice(0, 2).join(' '));
  if (iata && digits) {
    return { airline: iata, flight_number: digits };
  }

  const combined = parseFlightNumberInput(
    `${leg?.airline || ''}${leg?.flight_number || ''}`.replace(/\s+/g, '')
  );
  if (combined?.airline && combined?.flight_number) {
    return combined;
  }

  return null;
};

export const buildFlightLegLookupRequest = (leg) => {
  const parsed = resolveFlightLegIdentity(leg);
  if (!parsed || !leg?.departure_time) {
    return null;
  }

  return {
    parsed,
    request: {
      flight_number: `${parsed.airline}${parsed.flight_number}`,
      departure_date: String(leg.departure_time).slice(0, 10),
      origin: leg.departure_airport || undefined,
      destination: leg.arrival_airport || undefined,
    },
  };
};

export const getFlightLegLookupKey = (index, leg) => {
  const lookupConfig = buildFlightLegLookupRequest(leg);
  if (!lookupConfig) {
    return null;
  }

  const { request } = lookupConfig;
  return `${index}-${request.flight_number}-${request.departure_date}-${leg.departure_airport || ''}-${leg.arrival_airport || ''}`;
};

export const shouldAutoLookupFlightLeg = (index, leg, attemptedKeys = new Set()) => {
  if (!legNeedsRouteLookup(leg) || !leg?.departure_time) {
    return false;
  }

  const lookupKey = getFlightLegLookupKey(index, leg);
  return Boolean(lookupKey) && !attemptedKeys.has(lookupKey);
};

export const mergeFlightLegLookupResult = (leg, result, parsed) => {
  const airline = result.airline || parsed.airline || resolveAirlineIata(leg.airline, leg.airline_code) || leg.airline;
  const flightNumber = result.flight_number || parsed.flight_number || leg.flight_number;
  return {
    ...leg,
    airline,
    airline_code: resolveAirlineIata(airline) || leg.airline_code || airline,
    flight_number: flightNumber,
    departure_airport: result.departure_airport || leg.departure_airport || '',
    arrival_airport: result.arrival_airport || leg.arrival_airport || '',
    departure_time: result.departure_time
      ? normalizeDateTimeLocal(result.departure_time)
      : leg.departure_time,
    arrival_time: result.arrival_time
      ? normalizeDateTimeLocal(result.arrival_time)
      : leg.arrival_time,
  };
};

export const lookupFlightLeg = async (leg, options = {}) => {
  const lookupConfig = buildFlightLegLookupRequest(leg);
  if (!lookupConfig) {
    return { success: false, leg };
  }

  const result = await transportationAPI.lookupFlightByNumber({
    ...lookupConfig.request,
    signal: options.signal,
  });
  const resolved = Boolean(
    result?.success
    && result.departure_airport
    && result.arrival_airport
    && result.departure_airport !== result.arrival_airport
  );
  if (!resolved) {
    return { success: false, leg };
  }

  return {
    success: true,
    leg: mergeFlightLegLookupResult(leg, result, lookupConfig.parsed),
  };
};

export const lookupFlightLegs = async (legs = []) => {
  const resolvedLegs = [...legs];

  for (let index = 0; index < resolvedLegs.length; index += 1) {
    if (!legNeedsRouteLookup(resolvedLegs[index])) {
      continue;
    }
    const { leg } = await lookupFlightLeg(resolvedLegs[index]);
    resolvedLegs[index] = leg;
  }

  return resolvedLegs;
};
